'use client';

import get from 'lodash/get';
import { BASE_FILE_URL } from '@/common/constants';
import { formatDateVN } from '@/common/ultils';
import { CloseOutlined, DownloadOutlined, LeftOutlined, LinkOutlined, PlayCircleOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import Image from 'next/image';

function checkTypeImg(type, data, level = 0, originUrl) {
  const levels = ['thumbnail', 'small', 'medium', 'large', 'origin'];

  if (!originUrl) return '/img-default.png';

  // Xác định cấp hiện tại dựa vào type
  let currentLevel;
  if (type === 'mobile') currentLevel = 'small';
  else if (type === 'tablet') currentLevel = 'medium';
  else if (type === 'laptop') currentLevel = 'large';
  else if (type === 'desktop') currentLevel = 'large';
  else currentLevel = 'origin';

  // Tìm vị trí cấp hiện tại
  let idx = levels.indexOf(currentLevel);

  // Điều chỉnh idx theo level truyền vào, đảm bảo nằm trong khoảng [0, levels.length - 1]
  idx = Math.max(0, Math.min(levels.length - 1, idx + level));

  // Lấy url theo cấp đã xác định, ưu tiên từ cấp đã điều chỉnh xuống origin
  let url = null;
  for (let i = idx; i < levels.length; i++) {
    url = data?.[levels[i]];
    if (url) break;
  }
  // Nếu không có thì lấy imgaeUrls
  if (!url) url = originUrl;
  return `${BASE_FILE_URL}/${url}`;
}

const getMediaType = (item, urlIndex = 0) => {
  if (!item) return 'unknown';

  // Check for specific types first (longforms and infographics)
  if (item.type === 'longforms') {
    return 'longforms';
  }
  if (item.type === 'infographics') {
    return 'infographics';
  }

  // Check if it's a link
  if (item.isLink && item.link) {
    if (item.typeLink === 'youtube' || item.typeLink === 'facebook') {
      return 'video';
    } else if (item.typeLink) {
      const extension = item.link.split('.').pop().toLowerCase().split('?')[0];
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
        return 'video';
      } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
        return 'image';
      } else if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(extension)) {
        return 'audio';
      }
      return 'link';
    }
    return 'link';
  }

  // Handle fileUrls array
  if (!item.fileUrls || item.fileUrls.length === 0) return 'unknown';
  const fileUrl = item.fileUrls[urlIndex] || item.fileUrls[0];
  const extension = fileUrl.split('.').pop().toLowerCase();

  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
    return 'video';
  } else if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
    return 'image';
  } else if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(extension)) {
    return 'audio';
  }

  return 'unknown';
};

const getMediaUrl = (item, index = 0) => {
  if (item.isLink && item.link) {
    return item.link;
  }
  if (!item.fileUrls || item.fileUrls.length === 0) return null;
  const dataUrl = `${item.fileUrls[index] || item.fileUrls[0]}`;
  return dataUrl;
};

const MediaItem = ({ item, handleViewModal, deviceType }) => {
  const mediaUrl = getMediaUrl(item);
  const mediaType = getMediaType(item);

  const avatarUrl = checkTypeImg(deviceType, item?.sharpFileUrls?.filter((data) => data?.origin === mediaUrl)?.[0], -1, mediaUrl);

  return (
    <div
      onClick={() => handleViewModal && handleViewModal(item)}
      className="flex items-start justify-between space-x-3 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer">
      <div className="w-24 h-16 bg-gray-200 rounded overflow-hidden shrink-0">
        {mediaType !== 'video' ? (
          // <img
          //   src={avatarUrl}
          //   alt={mediaType}
          //   className="w-full h-full object-cover group-hover:scale-110 group-hover:brightness-110 hover:scale-105 transition-transform duration-300"
          // />
          <Image
            src={avatarUrl}
            alt={mediaType}
            fill
            className="w-full h-full object-cover group-hover:scale-110 group-hover:brightness-110 hover:scale-105 transition-transform duration-300 position-initial"
          />
        ) : (
          <div className="w-full h-full bg-linear-to-br from-red-400 to-red-600 flex items-center justify-center">
            <span className="text-white text-xs font-medium">{mediaType}</span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start space-x-2">
          <div
            className="text-md content-articles ck ck-content ck-editor__editable 
                              ck-rounded-corners ck-editor__editable_inline boldish-text ck-blurred line-clamp-2 overflow-y-hidden! leading-5!"
            dangerouslySetInnerHTML={{ __html: item.title || item.name }}
          />
        </div>
        <div className="mt-2">
          <span className="text-xs text-gray-500">{formatDateVN(item.createdAt || item.updatedAt)}</span>
        </div>
      </div>
    </div>
  );
};

const HomeMedia = ({ homeMedias, websiteCode, deviceType }) => {
  const t = useTranslations('');
  const [showPopup, setShowPopup] = useState(false);
  const [animatePopup, setAnimatePopup] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const handleViewModal = (item) => {
    setSelectedMedia(item);
    setShowPopup(true);
    setTimeout(() => setAnimatePopup(true), 10);
    setCurrentMediaIndex(0);
  };
  const handleNextMedia = () => {
    if (!selectedMedia) return;
    const maxIndex = selectedMedia.isLink ? 0 : (selectedMedia.fileUrls?.length || 1) - 1;
    setCurrentMediaIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
  };

  const handlePrevMedia = () => {
    if (!selectedMedia) return;
    const maxIndex = selectedMedia.isLink ? 0 : (selectedMedia.fileUrls?.length || 1) - 1;
    setCurrentMediaIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
  };
  const closePopup = () => {
    setAnimatePopup(false);
    setTimeout(() => setShowPopup(false), 300);
  };
  const renderPopupContent = () => {
    if (!selectedMedia) return null;

    const mediaType = getMediaType(selectedMedia, currentMediaIndex);
    const mediaUrl = getMediaUrl(selectedMedia, currentMediaIndex);
    const totalFiles = selectedMedia.isLink ? 1 : selectedMedia.fileUrls?.length || 1;
    const avatarUrl = checkTypeImg(deviceType, selectedMedia?.sharpFileUrls?.filter((data) => data?.origin === mediaUrl)?.[0], -1, mediaUrl);

    switch (mediaType) {
      case 'video':
        if (selectedMedia.isLink && selectedMedia.typeLink === 'youtube') {
          const videoId = getYouTubeVideoId(selectedMedia.link);
          return (
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          );
        }

        if (selectedMedia.isLink && selectedMedia.typeLink === 'facebook') {
          return (
            <div className="bg-linear-to-br from-blue-600 to-blue-800 p-8 rounded-2xl text-center">
              <PlayCircleOutlined className="text-6xl text-white mb-6" />
              <h3 className="text-2xl font-bold text-white mb-6">Facebook Video</h3>
              <a
                href={selectedMedia.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300">
                <span>Xem trên Facebook</span>
                <LinkOutlined />
              </a>
            </div>
          );
        }

        return (
          <div className="relative">
            <video src={`${BASE_FILE_URL}/${mediaUrl}`} controls autoPlay className="max-w-full max-h-[70vh] rounded-lg" />
            {totalFiles > 1 && (
              <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-lg text-sm">
                File {currentMediaIndex + 1} of {totalFiles}
              </div>
            )}
          </div>
        );

      case 'image':
        return (
          <div className="relative">
            <img
              src={avatarUrl}
              alt={selectedMedia?.name}
              className="max-w-full max-h-[80vh] rounded-lg hover:scale-105 transition-transform duration-300"
            />
            {totalFiles > 1 && (
              <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-lg text-sm">
                File {currentMediaIndex + 1} of {totalFiles}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-white text-center p-8">
            <p>Không thể hiển thị media này</p>
          </div>
        );
    }
  };
  return (
    <div className="border border-[#0000000f] rounded  bg-white w-full lg:w-[49.3%] vb-moi relative">
      <div className="title">
        <a href={`${websiteCode ? `/${websiteCode}` : ''}/da-phuong-tien/video`} className="font-normal">
          {t('home.titleContentMedia')}
        </a>
      </div>

      <div className="flex justify-between flex-col w-full h-full">
        <div className="space-y-1 pb-2 px-3">
          {get(homeMedias, 'results', []).map((item) => (
            <React.Fragment key={item.id}>
              <MediaItem item={item} handleViewModal={handleViewModal} deviceType={deviceType} />
              {showPopup && (
                <div
                  className={`fixed inset-0 z-50 p-4 flex items-center justify-center
                                bg-black/60 backdrop-blur-sm transition-opacity duration-300 
                                  ${animatePopup ? 'opacity-100' : 'opacity-0'}`}>
                  <div
                    className={`relative max-w-6xl w-full transform transition-all duration-300
                                  ${animatePopup ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0'}`}>
                    <button
                      onClick={closePopup}
                      className="absolute -top-6 right-[-22px] w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex cursor-pointer
                                    items-center justify-center text-white backdrop-blur-sm transition-all duration-300 
                                    hover:scale-110 hover:rotate-90 z-10">
                      <CloseOutlined className="text-xl" />
                    </button>

                    {selectedMedia?.fileUrls?.length > 1 && getMediaType(selectedMedia) !== 'audio' && (
                      <>
                        <button
                          onClick={handlePrevMedia}
                          className="fixed -left-2 top-1/2 -translate-y-1/2 
                                          w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full 
                                          flex items-center justify-center text-white backdrop-blur-sm 
                                          transition-all duration-300 hover:scale-110 z-50 cursor-pointer">
                          <LeftOutlined className="text-lg sm:text-xl" />
                        </button>
                        <button
                          onClick={handleNextMedia}
                          className="fixed -right-2 top-1/2 -translate-y-1/2 
                                         w-10 h-10 sm:w-12 sm:h-12 bg-white/10 hover:bg-white/20 rounded-full 
                                         flex items-center justify-center text-white backdrop-blur-sm 
                                         transition-all duration-300 hover:scale-110 z-50 cursor-pointer">
                          <RightOutlined className="text-lg sm:text-xl" />
                        </button>
                      </>
                    )}

                    <div className="bg-black/30 backdrop-blur-lg rounded-3xl p-6 border border-white/10 shadow-2xl overflow-y-auto max-h-[90vh]">
                      <div className="flex flex-col">
                        <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">{selectedMedia?.name}</h3>
                        <p className="text-lg text-gray-300 mb-6 leading-relaxed ">{selectedMedia?.content}</p>

                        <div className="mb-6">{renderPopupContent()}</div>

                        {selectedMedia?.type !== 'longforms' && (
                          <div className="flex flex-wrap gap-4">
                            {!selectedMedia?.isLink && (
                              <a
                                href={`${BASE_FILE_URL}/${getMediaUrl(selectedMedia, currentMediaIndex)}`}
                                download
                                className="flex items-center space-x-2 bg-linear-to-r from-orange-600 to-red-600 hover:from-orange-700 
                                             hover:to-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 
                                               hover:scale-105 shadow-lg">
                                <DownloadOutlined />
                                <span>{t('media.download')}</span>
                              </a>
                            )}

                            {selectedMedia?.isLink && (
                              <a
                                href={selectedMedia.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center space-x-2 bg-linear-to-r from-blue-600 to-purple-600 hover:from-blue-700 
                                             hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 
                                               hover:scale-105 shadow-lg">
                                <LinkOutlined />
                                <span>Mở liên kết</span>
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomeMedia;

